import React, { useCallback, useEffect, useRef, useState } from 'react';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { CatalogRoom, StopsellExtractionResult } from '~/types/stopsell';

import { pluralizeToString } from '~/utils/stringUtils';

import StopsellRoomGroup from './StopsellRoomGroup';

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (selected: StopsellExtractionResult) => void;
  extraction: StopsellExtractionResult;
  fileName: string;
  catalogRooms: Array<CatalogRoom>;
}

export default function StopsellReviewModal({ open, onClose, onApply, extraction, fileName, catalogRooms }: Props) {
  const [selected, setSelected] = useState<Array<Array<boolean>>>(() =>
    extraction.rooms.map((room) => room.dates.map(() => true)),
  );
  const [roomTypeOverrides, setRoomTypeOverrides] = useState<Record<number, string>>({});

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelected(extraction.rooms.map((room) => room.dates.map(() => true)));
      setRoomTypeOverrides({});
    }
    prevOpenRef.current = open;
  }, [open, extraction]);

  const allSelected = selected.every((room) => room.every(Boolean));
  const anySelected = selected.some((room) => room.some(Boolean));
  const selectAllIndeterminate = anySelected && !allSelected;

  const allSelectedRoomsMapped = extraction.rooms.every((room, i) => {
    const isRoomSelected = selected[i]?.some(Boolean);
    if (!isRoomSelected) return true;
    return !!(room.roomTypeId ?? roomTypeOverrides[i]);
  });

  const selectedRoomCount = selected.filter((room) => room.some(Boolean)).length;
  const selectedDateCount = selected.reduce((sum, room) => sum + room.filter(Boolean).length, 0);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelected(extraction.rooms.map((room) => room.dates.map(() => checked)));
    },
    [extraction],
  );

  const handleRoomToggleAll = useCallback((roomIndex: number, value: boolean) => {
    setSelected((prev) => prev.map((room, i) => (i === roomIndex ? room.map(() => value) : room)));
  }, []);

  const handleDateChange = useCallback((roomIndex: number, dateIndex: number, value: boolean) => {
    setSelected((prev) =>
      prev.map((room, i) => (i === roomIndex ? room.map((v, j) => (j === dateIndex ? value : v)) : room)),
    );
  }, []);

  const handleRoomTypeOverride = useCallback((roomIndex: number, roomTypeId: string) => {
    setRoomTypeOverrides((prev) => ({ ...prev, [roomIndex]: roomTypeId }));
  }, []);

  const handleApply = useCallback(() => {
    const result: StopsellExtractionResult = {
      rooms: extraction.rooms
        .map((room, i) => ({
          ...room,
          roomTypeId: roomTypeOverrides[i] ?? room.roomTypeId,
          dates: room.dates.filter((_, j) => selected[i][j]),
        }))
        .filter((room) => room.dates.length > 0),
    };
    onApply(result);
  }, [extraction, onApply, roomTypeOverrides, selected]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <EventBusyOutlinedIcon color="warning" />
          <Typography variant="h6">Stopsell Dates</Typography>
        </Stack>
        <Chip icon={<DescriptionOutlinedIcon />} label={fileName} size="small" sx={{ mt: 1 }} />
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            mb: 2,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={selectAllIndeterminate}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            }
            label={<Typography fontWeight={500}>Select all rooms</Typography>}
          />
        </Box>

        <Stack gap={1.5}>
          {extraction.rooms.map((room, roomIndex) => (
            <StopsellRoomGroup
              key={`${room.name}-${roomIndex}`}
              room={room}
              selected={selected[roomIndex]}
              catalogRooms={catalogRooms}
              selectedRoomTypeId={roomTypeOverrides[roomIndex] ?? room.roomTypeId ?? ''}
              onChange={(dateIndex, value) => handleDateChange(roomIndex, dateIndex, value)}
              onToggleAll={(value) => handleRoomToggleAll(roomIndex, value)}
              onRoomTypeChange={(roomTypeId) => handleRoomTypeOverride(roomIndex, roomTypeId)}
            />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{pluralizeToString('room', selectedRoomCount)}</strong>,{' '}
          <strong>{pluralizeToString('date range', selectedDateCount)}</strong> selected
        </Typography>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Tooltip
            title={anySelected && !allSelectedRoomsMapped ? 'All selected rooms must be mapped to a room type' : ''}
          >
            <span>
              <Button variant="contained" onClick={handleApply} disabled={!anySelected || !allSelectedRoomsMapped}>
                Apply Stopsell
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
